import { invariant } from '@seen/core/invariant'
import type { Label, LabelConstructor } from '@seen/core/models/label'
import { Button } from '@seen/ui'
import { sample } from 'lodash-es'
import { type ReactNode, useMemo } from 'react'
import { z } from 'zod'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { DialogClose } from '#src/components/core/dialog/DialogClose.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { SubmitButton } from '#src/components/core/form/SubmitButton.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { LabelColor } from '#src/components/LabelColor.tsx'
import { LabelOption } from '#src/components/LabelOption.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'

interface LabelDialogContentProps<T extends Label> {
  onSubmit: () => void
  label?: string
  labelClass: LabelConstructor<T>
}

const labelSchema = z.object({
  name: z.string().min(1, 'Label name is required'),
  color: z.string().min(1, 'Color is required'),
})

export function LabelDialogContent<T extends Label>({
  onSubmit,
  label,
  labelClass,
}: LabelDialogContentProps<T>): ReactNode {
  const { data: labelInstance } = useRead(labelClass, label)

  if (label && !labelInstance) {
    return (
      <div className="h-48">
        <Loader />
      </div>
    )
  }

  return (
    <LabelDialogContentInner
      onSubmit={onSubmit}
      label={labelInstance}
      labelClass={labelClass}
    />
  )
}

interface LabelDialogContentInnerProps<T extends Label> {
  onSubmit: () => void
  label?: T
  labelClass: LabelConstructor<T>
}

function LabelDialogContentInner<T extends Label>({
  onSubmit,
  label,
  labelClass,
}: LabelDialogContentInnerProps<T>): ReactNode {
  const isReadOnly = useIsReadOnly()
  const createMutation = useCreateMutation()
  const patchMutation = usePatchMutation<T>()
  const currentPractitioner = useCurrentPractitioner()

  const defaultColor = useMemo(() => {
    const c = sample(labelClass.defaultColors)?.color
    invariant(c, 'Missing default color')
    return c
  }, [labelClass])

  const form = useForm({
    schema: labelSchema,
    defaultValues: {
      name: label?.name ?? '',
      color: label?.color ?? defaultColor,
    },
  })

  const submit = form.handleSubmit((values) => {
    if (label) {
      patchMutation.mutate(
        {
          instance: label,
          mutateFn: (instance) => {
            instance.name = values.name
            instance.color = values.color
          },
        },
        {
          onSuccess: onSubmit,
        }
      )
    } else {
      createMutation.mutate(
        new labelClass({
          name: values.name,
          color: values.color,
          author: currentPractitioner.reference,
        }),
        {
          onSuccess: onSubmit,
        }
      )
    }
  })

  const colorOptions = useMemo(
    () =>
      labelClass.defaultColors.map(({ name, color }) => ({
        label: (
          <LabelOption
            color={color}
            label={name}
            isSelected={form.watch('color') === color}
          />
        ),
        value: color,
        keywords: [name],
      })),
    [labelClass.defaultColors, form]
  )

  return (
    <Form onSubmit={submit}>
      <DialogHeader className="flex flex-row-reverse items-center py-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <RichTextEditor
              placeholder="Label name"
              variant="unstyled"
              className="flex-1"
              contentClassName="text-xl font-bold"
              {...field}
            />
          )}
        />
        <FormField
          control={form.control}
          name="color"
          render={({ field: { value, onChange, ...rest } }) => (
            <Combobox options={colorOptions} onSelect={onChange}>
              <Button
                variant="unstyled"
                className="size-10 items-center justify-center rounded-full border hover:border-border-hover"
                {...rest}
              >
                <LabelColor color={value} />
              </Button>
            </Combobox>
          )}
        />
      </DialogHeader>
      <DialogFooter>
        <DialogClose asChild>
          <Button variant="outline">Cancel</Button>
        </DialogClose>
        <SubmitButton
          submitting={createMutation.isPending || patchMutation.isPending}
          disabled={isReadOnly}
        >
          {label ? 'Update label' : 'Create label'}
        </SubmitButton>
      </DialogFooter>
    </Form>
  )
}
